'use client';

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMainCoach } from '@/components/user/main-coach-provider';
import { useUser } from '@/components/user/user-provider';
import { cn } from '@/lib/utils';
import { PiChalkboardTeacherFill } from 'react-icons/pi';
import { FiChevronDown } from 'react-icons/fi';

type CoachSwitcherVariant = 'sidebar' | 'navbar';

export function CoachSwitcher({ variant = 'sidebar' }: { variant?: CoachSwitcherVariant }) {
  const { user } = useUser();
  const { mainCoach, coaches, setMainCoachId } = useMainCoach();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; minWidth: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isCoach = user && !('error' in user) && user.is_coach;
  const showSwitcher = !isCoach && coaches.length > 0;
  const canSwitch = coaches.length > 1;

  useEffect(() => {
    if (!open) {
      setDropdownRect(null);
      return;
    }
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const isSidebar = variant === 'sidebar';
    const gap = 4;
    const minW = 180;
    if (isSidebar) {
      setDropdownRect({
        minWidth: Math.max(minW, rect.width),
        left: rect.left,
        top: rect.bottom + gap,
      });
    } else {
      setDropdownRect({
        minWidth: minW,
        left: rect.left + rect.width / 2,
        top: rect.top - gap,
      });
    }
  }, [open, variant]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      const insideTrigger = ref.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideTrigger && !insideDropdown) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectCoach = (id: number) => {
    setMainCoachId(id);
    setOpen(false);
    navigate(`/plans/${id}`);
  };

  if (!showSwitcher) return null;

  const isSidebar = variant === 'sidebar';
  const isNavbar = variant === 'navbar';

  return (
    <div ref={ref} className={cn('relative', isSidebar && 'w-full', isNavbar && 'flex-1 min-w-0 flex flex-col items-center justify-center')}>
      {canSwitch ? (
        <>
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={mainCoach ? `Treinador: ${mainCoach.name}. Trocar treinador.` : 'Trocar treinador'}
            aria-expanded={open}
            aria-haspopup="true"
            className={cn(
              'flex items-center gap-2 rounded-lg text-left transition-colors',
              isSidebar && 'w-full py-2.5 px-3 text-sm font-medium text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
              isNavbar && 'flex-col gap-0.5 py-1 px-1 min-w-0 w-full text-muted-foreground hover:text-foreground'
            )}
          >
            {mainCoach?.photo ? (
              <img
                src={mainCoach.photo}
                alt=""
                className={cn('rounded-full shrink-0 object-cover', isSidebar ? 'w-8 h-8' : 'w-6 h-6')}
              />
            ) : (
              <span className={cn('flex shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary', isSidebar ? 'w-8 h-8' : 'w-6 h-6')}>
                <PiChalkboardTeacherFill className={isSidebar ? 'w-4 h-4' : 'w-5 h-5'} />
              </span>
            )}
            {!isNavbar && (
              <>
                <span className={cn('min-w-0 truncate', 'max-w-[120px]')}>
                  {mainCoach?.name ?? 'Treinador'}
                </span>
                <FiChevronDown className={cn('shrink-0 transition-transform', open && 'rotate-180', isSidebar ? 'w-4 h-4' : 'w-3.5 h-3.5')} />
              </>
            )}
            {isNavbar && (
              <>
                <span className="text-[10px] font-medium truncate max-w-[64px]">Treinador</span>
                <span className={cn('w-1.5 h-1.5 rounded-full transition-opacity', open ? 'bg-primary opacity-100' : 'opacity-0')} />
              </>
            )}
          </button>

          {open &&
            dropdownRect &&
            typeof document !== 'undefined' &&
            createPortal(
              <div
                ref={dropdownRef}
                role="menu"
                className="fixed z-[100] rounded-lg border border-border bg-card py-1 shadow-lg max-h-[min(70vh,400px)] overflow-y-auto"
                style={{
                  minWidth: dropdownRect.minWidth,
                  left: isSidebar ? dropdownRect.left : undefined,
                  top: isSidebar ? dropdownRect.top : undefined,
                  ...(isNavbar && {
                    left: dropdownRect.left,
                    top: dropdownRect.top,
                    transform: 'translate(-50%, -100%)',
                  }),
                }}
              >
                <div className={cn('px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider', isNavbar && 'hidden')}>
                  Trocar treinador
                </div>
                {coaches.map((c, index) => (
                  <button
                    key={`coach-${c.id}-${index}`}
                    type="button"
                    role="menuitem"
                    onClick={() => handleSelectCoach(c.id)}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-left transition-colors',
                      mainCoach?.id === c.id
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-muted'
                    )}
                  >
                    {c.photo ? (
                      <img src={c.photo} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                    ) : (
                      <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <PiChalkboardTeacherFill className="w-4 h-4 text-muted-foreground" />
                      </span>
                    )}
                    <span className="min-w-0 truncate">{c.name}</span>
                  </button>
                ))}
                <div className="border-t border-border mt-1 pt-1">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setOpen(false);
                      navigate('/coaches');
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    Ver todos os treinadores
                  </button>
                </div>
              </div>,
              document.body
            )}
        </>
      ) : (
        <div
          className={cn(
            'flex items-center gap-2',
            isSidebar && 'py-2.5 px-3 text-sm text-sidebar-foreground/80',
            isNavbar && 'flex-col gap-0.5 py-1 px-2 text-muted-foreground'
          )}
        >
          {mainCoach?.photo ? (
            <img
              src={mainCoach.photo}
              alt=""
              className={cn('rounded-full shrink-0 object-cover', isSidebar ? 'w-8 h-8' : 'w-6 h-6')}
            />
          ) : (
            <span className={cn('flex shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary', isSidebar ? 'w-8 h-8' : 'w-6 h-6')}>
              <PiChalkboardTeacherFill className={isSidebar ? 'w-4 h-4' : 'w-5 h-5'} />
            </span>
          )}
          {!isNavbar && (
            <span className="min-w-0 truncate max-w-[120px]">
              {mainCoach?.name ?? 'Treinador'}
            </span>
          )}
          {isNavbar && (
            <span className="text-[10px] font-medium truncate max-w-[64px]">Treinador</span>
          )}
        </div>
      )}
    </div>
  );
}
